import { RouterLoadBoundary } from './router-load-boundary';
import { useApmmRouterConfig } from './router/router-config';
import { TradeConfirm } from './trade-confirm';
import { lazy, Suspense, useMemo, useState, type ComponentProps } from 'react';

type Props = ComponentProps<typeof TradeConfirm>;
type RouterConfigOn = { router: `0x${string}`; factory: `0x${string}`; ctf: `0x${string}` };

// The switch is checked at build time as well: a build without the router address never imports the
// router view, so none of its code ships (the condition folds to a constant in a production build).
const loadRouterView = import.meta.env.VITE_DPIN_ROUTER_ADDRESS ? () => import('./router/router-trade-confirm') : undefined;

const RouterPath = ({
  load,
  config,
  ...props
}: Props & { load: NonNullable<typeof loadRouterView>; config: RouterConfigOn }) => {
  const [attempt, setAttempt] = useState(0);
  // A failed lazy component stays failed, so a retry makes a new one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const View = useMemo(() => lazy(load), [load, attempt]);
  return (
    <RouterLoadBoundary key={attempt} onRetry={() => setAttempt(a => a + 1)}>
      <Suspense fallback={null}>
        <View {...props} config={config} />
      </Suspense>
    </RouterLoadBoundary>
  );
};

/**
 * Chooses the confirm button. Switch off: the unchanged direct path. Switch on: the router flow, and the
 * direct path is never a fallback (a paused router greys the button, a failed load shows an error).
 */
export const TradeConfirmEntry = (props: Props) => {
  const config = useApmmRouterConfig();
  if (!loadRouterView || config.status !== 'on') return <TradeConfirm {...props} />;
  return <RouterPath {...props} load={loadRouterView} config={config} />;
};
